import type { CookieSerializeOptions } from 'cookie'
import Cookies from 'js-cookie'
import type { NextRequest, NextResponse } from 'next/server'
import { isBrowser } from './isBrowser'
import { randomSelect } from './randomSelect'

export type AbTestOptions<T extends string> = {
  getEnabled: () => boolean
  /**
   * custom dimensionを伴う場合設定
   */
  customDimensionName?: string
  /**
   * テスト名と重みのマップ e.g. `{testA:1, testB:2}` (1:2の比率)
   */
  variantMap: Record<T, number>
  /**
   * ABテストを分岐するためのcookie名
   */
  cookieName: string
  /**
   * cookieのオプション(期限等)
   */
  cookieOptions?: CookieSerializeOptions
}

const noopMiddleware = (_: NextRequest, res: NextResponse) => res
const noopGetVariant = () => undefined

/**
 * ABテストのfactory関数
 * @param options ABテストのオプション
 * @returns Next.js middleware用の関数とブラウザ用のクッキーからどのテストを実施するかの関数
 * @example
 * ```ts
 * const { middleware, getVariant } = createAbTest({
 *   cookieName: 'imageAbTest',
 *   variantMap: { color: 1, mono: 1 }
 * });
 * export const imageAbTestMiddleware = middleware;
 * export const getImageAbTestVariant = getVariant;
 * // middleware
 * export const middleware = (req) => {
 *  return imageAbTestMiddleware(req, NextResponse.next())
 * }
 * // component (only csr)
 * export const Component = () =>
 *   getImageAbTestVariant() === 'color'
 *     ? <img src="color.png" />
 *     : <img src="mono.png" />
 * // component (ssr/csr)
 * const useImageAbTestVariant = createVariantHook(getImageAbTestVariant)
 * export const Component = () =>
 *   useImageAbTestVariant() === 'color'
 *     ? <img src="color.png" />
 *     : <img src="mono.png" />
 * ```
 */
export const createAbTest = <T extends string>({
  getEnabled = () => true,
  customDimensionName,
  variantMap,
  cookieName,
  cookieOptions,
}: AbTestOptions<T>): {
  /**
   * middleware内でcookieを設定する関数
   */
  middleware: (req: NextRequest, res: NextResponse) => NextResponse
  /**
   * CSRで表示を分岐する関数
   */
  getVariant: () => T | undefined
} => {
  if (!getEnabled()) {
    // ABテストが無効な場合
    return { middleware: noopMiddleware, getVariant: noopGetVariant }
  }

  if (isBrowser) {
    window.dataLayer = window.dataLayer || []
    const variant = Cookies.get(cookieName)
    if (customDimensionName !== undefined)
      window.dataLayer.push({ [customDimensionName]: variant })
    return {
      middleware: noopMiddleware, // noop(client)
      getVariant: () => variant as T,
    }
  }

  return {
    middleware: (req, res) => {
      if (req.cookies.get(cookieName) != null) return res
      res.cookies.set(
        cookieName,
        randomSelect(Object.entries(variantMap)) ?? '',
        cookieOptions
      )
      return res
    },
    getVariant: noopGetVariant, // noop(server)
  }
}
